import type { RateKind } from '../terms/types'
import type {
  EarnAmount,
  EarnAvailability,
  EarnBasket,
  EarnBasketLeg,
  EarnGating,
  EarnMarket,
  EarnRate,
} from './types'
import { earnUidFromMarketUid } from './uid'
import {
  earnMarketLabel,
  isIlliquid,
  resolveEarnIdentity,
  venueBrand,
} from './labels'
import { listaCollateralProvider } from '@1delta/data-sdk'
import { addr, num, str } from './normalize'

/**
 * Lending half of the `/earn` normalizer: one `/pools/latest` row →
 * `EarnMarket`, projecting the SUPPLY side only.
 *
 * The borrow side is not dropped, it is *pointed at*: `refs.marketUid` +
 * `refs.borrowable` let a consumer jump to `/v1/data/lending/*` for the full
 * market. Duplicating the borrow economics here would double the payload to
 * serve a question this endpoint does not ask.
 *
 * The origin reshapes the SDK's flat `PoolData` into a partly-nested form
 * (`caps`, `flags`, `underlyingInfo`), and the origin lives in another repo —
 * so every field is read tolerantly in BOTH shapes. That is deliberate
 * defensiveness, not indecision: a field that moves nests silently, and a
 * silently-missing `isFrozen` would advertise a dead market as depositable.
 *
 * See EARN_ENDPOINT_PLAN.md §4.
 */

/** The origin's `/pools/latest` item (`PoolWithMeta`). Permissive by design. */
export interface PoolSourceRow {
  /** Stamped by the public-data parsers. REQUIRED — see `earnMarketFromPool`. */
  marketUid?: string
  lender?: string
  lenderKey?: string
  chainId?: string
  poolId?: string
  underlying?: string
  name?: string
  asset?: {
    address?: string
    symbol?: string
    decimals?: number
    logoURI?: string
    assetGroup?: string
  }
  underlyingInfo?: {
    asset?: {
      address?: string
      symbol?: string
      decimals?: number
      logoURI?: string
    }
    assetGroup?: string
    prices?: { priceUsd?: number | string }
  }
  decimals?: number
  /** Origin-computed `depositRate + intrinsicYield`. */
  apr?: number | string
  price?: number | string
  depositRate?: number | string
  intrinsicYield?: number | string
  variableBorrowRate?: number | string
  rewards?: Array<{ asset?: string; depositRate?: number | string }>
  totalDeposits?: string | number
  totalDepositsUSD?: number | string
  totalDepositsUsd?: number | string
  totalLiquidity?: number | string
  totalLiquidityUSD?: number | string
  totalLiquidityUsd?: number | string
  utilization?: number | string
  /**
   * NESTED on the origin response — `risk: { score, label, breakdown }` — not
   * flat `riskScore`. Reading the flat form silently yielded no risk at all.
   */
  risk?: { score?: number | string; label?: string }
  /**
   * ROW-LEVEL, and deliberately not under `fluid`: the position's composition
   * is pool-controlled. Lender-agnostic by design — Lista SmartLP and GMX
   * GM/GLV are the same shape.
   */
  autoBalanced?: boolean
  /**
   * Fluid's smart-vault descriptor. The ONLY source of leg order today, and
   * the reason `basket.legs` can be populated at all on the lending half.
   */
  fluid?: {
    isSmartCol?: boolean
    isSmartDebt?: boolean
    collateralPair?: string[]
    debtPair?: string[]
    basketSupplyRate?: number | string
    basketBorrowRate?: number | string
    supplyDexTradingRate?: number | string
    borrowDexTradingRate?: number | string
    [key: string]: unknown
  }
  /**
   * Oracle provenance per feed. `priceDescription` ("BTC / USD") is the only
   * field the label uses — it is what distinguishes two markets on the same
   * pair at the same LLTV.
   */
  oracleInfo?: { feeds?: Array<{ priceDescription?: string; oracle?: string }> }
  /**
   * The lender's own identity for this market — `{ key, name, logoURI }`.
   * `name` is the best market label available (`Morpho cbBTC-USDC 86`), and is
   * populated for every lender family in the live listing.
   */
  lenderInfo?: {
    key?: string
    name?: string
    logoURI?: string
    /**
     * The protocol this market belongs to, from `lender-labels.json`'s
     * `protocols` map. Absent until that reaches the public-data fetchers, at
     * which point it supersedes the SDK's fallback table.
     */
    protocol?: string
  }
  supplyCap?: number | string
  caps?: { supplyCap?: number | string; borrowCap?: number | string }
  collateralActive?: boolean
  borrowingEnabled?: boolean
  depositsEnabled?: boolean
  isActive?: boolean
  isFrozen?: boolean
  flags?: {
    collateralActive?: boolean
    borrowingEnabled?: boolean
    depositsEnabled?: boolean
    isActive?: boolean
    isFrozen?: boolean
  }
  [key: string]: unknown
}

/**
 * Lending rate mechanism by lender family.
 *
 * Defaults to `variable-curve` because the overwhelming majority of the book is
 * a utilization IRM. The fixed-term and CDP families are the exceptions and are
 * matched by key prefix — the same shape the lender-registry predicates use,
 * but without importing them (this package is upstream of the registry helpers
 * that would resolve them properly; the term-sheet adapter is what does this
 * exactly, and it supersedes this table).
 */
const FIXED_TERM_PREFIXES = [
  'EXACTLY',
  'TELLER',
  'TERM_FINANCE',
  'TERMMAX',
  'MORPHO_MIDNIGHT',
]
const USER_SET_RATE_PREFIXES = [
  'LIQUITY',
  'USDAF',
  'FELIX',
  'NERITE',
  'QUILL',
  'ENOSYS_LOANS',
  'SONETA',
  'EBISU',
]

/**
 * Collateral symbols per venue, derived from the listing ITSELF.
 *
 * An isolated market is a (collateral, loan) pair, but the fetcher emits it as
 * TWO rows — `Collateral cbBTC` and `Loan USDC` — each naming only its own leg.
 * The pairing is nonetheless recoverable without any extra fetch, because both
 * legs share the per-market venue key (`MORPHO_BLUE_<id>`, `FLUID_1_11`).
 * Grouping by venue and keeping the collateral-enabled symbols reconstructs
 * exactly the input `earnMarketLabel` needs.
 *
 * Verified against the live chain-1 listing (1,823 rows): 329 Morpho venues,
 * 264 of them a clean 2-row pair; Fluid 97, Silo 55, Resupply 13, Frankencoin
 * 11 the same shape. Shared pools land on the other side of the same rule —
 * Gearbox averages 3.5 collaterals per pool, a Compound III comet ~10, Aave V3
 * ~13 — so they are named by asset alone, which is correct.
 *
 * `collateralActive` is published by EVERY lender family in that listing (zero
 * undefined), so there is no flag-absent fallback to get wrong.
 *
 * **Pass the WHOLE scope's rows, not a page.** Given a page, a shared pool
 * looks isolated and gets a confidently wrong "vs" label — worse than no
 * label, because it names one arbitrary collateral of thirty.
 *
 * Keyed by (chain, venue) rather than venue alone: a per-market key is only
 * chain-unique when the lender bakes an id into it, and `AAVE_V3` is the same
 * string on 20 chains. Cross-chain merging would not produce a wrong pairing
 * (the count only grows, so a market degrades to its plain name) but the key
 * costs nothing and removes the question.
 */
export function collateralSymbolsByVenue(
  rows: readonly PoolSourceRow[],
  fallbackChainId?: string,
): Map<string, string[]> {
  const byVenue = new Map<string, Set<string>>()
  for (const row of rows) {
    const venue = str(row.lender) ?? str(row.lenderKey)
    const chainId = str(row.chainId) ?? fallbackChainId
    if (!venue || !chainId) continue
    const collateralActive = row.flags?.collateralActive ?? row.collateralActive
    if (collateralActive !== true) continue
    const symbol =
      str(row.underlyingInfo?.asset?.symbol) ?? str(row.asset?.symbol)
    if (!symbol) continue
    const key = venueGroupKey(chainId, venue)
    let set = byVenue.get(key)
    if (!set) byVenue.set(key, (set = new Set()))
    set.add(symbol)
  }
  return new Map([...byVenue].map(([key, set]) => [key, [...set]]))
}

/** Internal key for {@link collateralSymbolsByVenue}. Never serialized. */
function venueGroupKey(chainId: string, venue: string): string {
  return `${chainId}::${venue}`
}

/**
 * Normalize one origin pool row.
 *
 * Returns `undefined` when the row carries no `marketUid`. **The uid is never
 * reconstructed** — rebuilding it as `lender:chainId:underlying` is only
 * correct for the default-format lenders and silently mints a wrong key for
 * Compound V2 (needs the cToken) and Dolomite (needs the integer marketId).
 * A wrong term sheet is a display bug; a wrong uid routes a deposit to the
 * wrong market. Drop the row and let the caller log it.
 *
 * `venueCollaterals` is the whole map from {@link collateralSymbolsByVenue} —
 * the row is looked up here so a caller cannot key it wrong. Omitting it is
 * legal and falls back to the fetcher's own name, which is how 300 rows came to
 * read "Loan USDC" in production.
 *
 * See EARN_ENDPOINT_PLAN.md §3.2 and §4.1.
 */
/**
 * The basket descriptor for a LENDING row.
 *
 * Only Fluid smart vaults reach this today, and only they can: the descriptor
 * needs the pool's own leg ORDER, and `fluid.{collateralPair,debtPair}` is the
 * one place any lender publishes it. Lista SmartLP is the next member — its
 * roster lives in `lista-collateral-providers.json` and is not on the row yet,
 * which is why this reads a lender-agnostic `autoBalanced` flag first and only
 * then looks for legs: a provider that sets the flag without publishing legs
 * still gets a correct, if thinner, descriptor rather than none.
 *
 * The SUPPLY side is the only one an earn row describes — an earn row is a
 * place to put money — so a T3 (simple collateral, smart DEBT) yields no
 * basket here even though it is a smart vault. That is not an omission: on the
 * earn surface a T3's deposit really is a plain single-asset supply.
 */
function resolveBasket(row: PoolSourceRow): EarnBasket | undefined {
  /**
   * LISTA SMARTLP — the second lender family, and the one that proves the
   * descriptor is not Fluid-shaped.
   *
   * It sets no `autoBalanced` flag of its own (nothing upstream does), so it is
   * resolved from the roster instead: Moolah gates these markets behind a
   * `SmartProvider` that zaps a two-coin StableSwap pool, and the collateral
   * receipt is `onlyMoolah`-transferable — a holder literally cannot take one
   * coin out, which is the strongest form of the claim `autoBalanced` makes.
   *
   * Checked BEFORE the flag, because the whole point is that a provider can be
   * a basket without anyone having stamped it.
   */
  const provider = listaCollateralProvider(
    row.chainId ?? '',
    str((row.params as any)?.market?.collateralProvider),
  )
  if (provider?.kind === 'smart-lp' && provider.coins) {
    const rowAsset = addr(row.underlying) ?? addr(row.asset?.address)
    // Only the COLLATERAL row is the pool position. The market's loan-token row
    // is an ordinary single-asset supply and must not be described as a basket.
    if (rowAsset && rowAsset === provider.collateralToken.toLowerCase()) {
      return {
        // The row's asset IS the receipt — one row for the whole position,
        // the opposite of Fluid's per-leg rows.
        rowAsset: 'positionUnit',
        // `dex.coins(i)` order, NOT the collateral symbol's order:
        // "USDT & USDe-SmartLP" is [USDe, USDT].
        legs: provider.coins.map((c) => ({
          address: c.address.toLowerCase(),
          symbol: c.symbol,
          decimals: c.decimals,
        })),
        autoBalanced: true,
        ...(provider.dexLp
          ? {
              positionUnit: {
                kind: 'lpToken' as const,
                address: provider.dexLp.toLowerCase(),
                decimals: provider.collateralDecimals,
              },
            }
          : {}),
        pool: 'lista-smartlp',
      }
    }
  }

  if (row.autoBalanced !== true) return undefined
  const f = row.fluid
  // A smart DEBT side with a simple collateral side is not a basket DEPOSIT.
  if (f && f.isSmartCol !== true) return undefined

  const legs: EarnBasketLeg[] = (f?.collateralPair ?? [])
    .map((a) => addr(a))
    .filter((a): a is string => !!a)
    .map((address) => ({ address }))

  /**
   * The row must BE one of the collateral legs.
   *
   * A market's LOAN token is an ordinary single-asset deposit even when the
   * market's COLLATERAL is an LP — supplying USDT to a vault that takes a
   * reUSD+USDT LP as collateral carries none of the basket's drift. Describing
   * that row as a basket would hide it behind the `?lp=include` gate for no
   * reason and warn about exposure the depositor does not have.
   *
   * Only checked when the legs are known; a provider that flags the side
   * without publishing them still gets the thin descriptor.
   */
  if (legs.length > 0) {
    const rowAsset = addr(row.underlying) ?? addr(row.asset?.address)
    if (!rowAsset || !legs.some((l) => l.address === rowAsset)) return undefined
  }

  return {
    // Fluid emits one row PER LEG of the pool, so this row is a leg and a
    // consumer that sums the listing without deduping counts the position
    // twice. Lista/GMX are the `positionUnit` shape and will say so.
    rowAsset: 'leg',
    legs,
    autoBalanced: true,
    ...(f?.supplyDexTradingRate != null || f?.collateralPair
      ? { pool: 'fluid-dex' }
      : {}),
  }
}

export function earnMarketFromPool(
  row: PoolSourceRow,
  fallbackChainId?: string,
  venueCollaterals?: ReadonlyMap<string, string[]>,
): EarnMarket | undefined {
  const marketUid = str(row.marketUid)
  if (!marketUid) return undefined

  let earnUid: string
  try {
    earnUid = earnUidFromMarketUid(marketUid)
  } catch {
    return undefined
  }

  const chainId = str(row.chainId) ?? fallbackChainId
  const venue = str(row.lender) ?? str(row.lenderKey)
  if (!chainId || !venue) return undefined

  const flags = row.flags ?? {}
  const assetInfo = row.underlyingInfo?.asset ?? row.asset ?? {}
  const underlying =
    addr(assetInfo.address) ?? addr(row.underlying) ?? addr(row.poolId)
  if (!underlying) return undefined

  const decimals = num(assetInfo.decimals) ?? num(row.decimals) ?? 18
  const priceUsd = num(row.underlyingInfo?.prices?.priceUsd) ?? num(row.price)

  // Lending rates are ALREADY percent at the origin — no scaling here. The
  // fraction-vs-percent hazard is exclusively a vault-side problem
  // (see `fromVault.FRACTION_RATE_PROVIDERS`).
  const base = num(row.depositRate)
  const intrinsic = num(row.intrinsicYield)
  const rewards = sumRewardAprs(row.rewards)

  const marketOwn = sum(base, rewards)
  const rate: EarnRate = {
    // Prefer our own sum over the origin's `apr`, because `apr` is documented
    // as `depositRate + intrinsicYield` and therefore EXCLUDES incentives —
    // using it as the headline would under-report every incentivized market.
    total: sum(base, intrinsic, rewards) ?? num(row.apr) ?? 0,
    base,
    rewards,
    intrinsic,
    marketOwn,
    passthrough: isPassthrough(marketOwn, intrinsic),
    kind: resolveRateKind(venue),
    source: 'chain',
  }

  const availability = resolveAvailability(row, flags, rate.total)
  const basket = resolveBasket(row)

  return {
    earnUid,
    chainId,
    venue,
    venueKind: 'lending',
    // The family brand ('Morpho Blue'), not the per-market key. The specific
    // market identity survives on `name`.
    brand: venueBrand(venue),
    // Same resolver as the vault half, so `protocol.key` means one thing
    // across the listing: the STABLE family key, never the per-market venue.
    // No lender publishes a curator today, hence the undefined.
    ...resolveEarnIdentity(venue, undefined, str(row.lenderInfo?.protocol)),
    // Pair-aware: "USDT · vs wstETH" for an isolated market, plain "USDC" for
    // a shared pool. The fetcher's own name is only the fallback — it is the
    // leg-local "Loan USDC", which a chain repeats across 300 markets and
    // which says nothing about WHICH market this is. Identical to what the
    // origin's SQL route builds, so both paths agree.
    name: earnMarketLabel({
      assetSymbol: str(assetInfo.symbol),
      lenderMarketName: str(row.lenderInfo?.name),
      fetcherName: str(row.name),
      venue,
      // The venue's collaterals, UNFILTERED — the label removes this row's own
      // leg itself. Filtering here made the collateral row of a 1-collateral
      // market look like a 0-collateral one, so the two legs of one market
      // disagreed about whether it was isolated.
      collateralSymbols: venueCollaterals?.get(venueGroupKey(chainId, venue)),
      fallbackName: str(row.name),
    }),
    // Never re-derived — the uid's third segment as the origin minted it.
    ref: marketUid.split(':')[2],
    logoURI: str(assetInfo.logoURI),

    asset: {
      address: underlying,
      symbol: str(assetInfo.symbol) ?? '',
      decimals,
      assetGroup:
        str(row.underlyingInfo?.assetGroup) ?? str(row.asset?.assetGroup),
      priceUsd,
    },
    // A lending supply position is an aToken/cToken/eToken balance, but the
    // origin listing does not carry that address — and guessing one would be
    // worse than omitting it. Consumers that need it resolve via `marketUid`.
    shareToken: undefined,

    rate,
    // Lending amounts arrive PRE-FORMATTED (`parseRawAmount` = `formatUnits`),
    // so `formatted` is populated and `raw` is genuinely absent. Do NOT
    // back-fill `raw` by re-scaling — the round-trip loses precision and would
    // fabricate base units the origin never sent.
    tvl: amount(
      row.totalDeposits,
      row.totalDepositsUSD ?? row.totalDepositsUsd,
    ),
    liquidity: amount(
      row.totalLiquidity,
      row.totalLiquidityUSD ?? row.totalLiquidityUsd,
    ),
    depositCapacity: undefined,
    utilization: num(row.utilization),

    // Lending withdrawals are same-block, bounded by available liquidity —
    // which is exactly `instant-capped`, not `instant`. A pool at 100 %
    // utilization cannot be exited, and calling that `instant` is the kind of
    // small lie that produces a stuck user.
    exit: { mode: 'instant-capped', settlement: 'sync' },
    availability,
    risk: {
      score: num(row.risk?.score),
      label: str(row.risk?.label),
      illiquid: isIlliquid({
        exitMode: 'instant-capped',
        tvlUsd: num(row.totalDepositsUSD ?? row.totalDepositsUsd),
        liquidityUsd: num(row.totalLiquidityUSD ?? row.totalLiquidityUsd),
      }),
    },

    ...(basket ? { basket } : {}),

    capabilities: [],
    refs: {
      marketUid,
      // Carried for `disambiguateEarnNames`, which is the only thing that
      // reads them — two markets on the same pair at the same LLTV differ by
      // nothing else, and one may price its collateral off a feed that ignores
      // a depeg in it.
      oracle: addr(row.oracleInfo?.feeds?.find((f) => f?.oracle)?.oracle),
      oracleDescription: str(
        row.oracleInfo?.feeds?.find((f) => f?.priceDescription)
          ?.priceDescription,
      ),
      borrowable: (flags.borrowingEnabled ?? row.borrowingEnabled) === true,
    },
  }
}

/**
 * Below this (in percent) a venue's own yield is treated as nothing.
 *
 * Not zero: utilization IRMs report dust — a market at 0.3 % utilization
 * quotes something like 0.004 %, which is economically nil but would defeat an
 * `=== 0` test and let every LST market back into the list.
 */
export const PASSTHROUGH_RATE_EPSILON = 0.01

/**
 * Is this row's yield entirely the asset's own?
 *
 * Requires `intrinsic > 0`: a market paying nothing on an asset that also pays
 * nothing is not pass-through, it is a dead or collateral-only market, and
 * `availability.gating` already says so. Conflating the two would hide a
 * frozen USDC reserve behind a label about staking yield.
 */
function isPassthrough(
  marketOwn: number | undefined,
  intrinsic: number | undefined,
): boolean {
  if (intrinsic === undefined || intrinsic <= 0) return false
  return (marketOwn ?? 0) < PASSTHROUGH_RATE_EPSILON
}

function resolveRateKind(venue: string): RateKind {
  const key = venue.toUpperCase()
  if (FIXED_TERM_PREFIXES.some((p) => key.startsWith(p))) return 'fixed-term'
  if (USER_SET_RATE_PREFIXES.some((p) => key.startsWith(p))) return 'user-set'
  return 'variable-curve'
}

/**
 * Availability for a lending reserve.
 *
 * `collateral-only` is the one gating value that coexists with
 * `canDeposit: true` — it describes ECONOMICS, not permission. The deposit
 * genuinely works; it just earns nothing, and a consumer building an earn list
 * wants to filter those out rather than show a 0.00 % row at the bottom.
 */
function resolveAvailability(
  row: PoolSourceRow,
  flags: NonNullable<PoolSourceRow['flags']>,
  totalRate: number,
): EarnAvailability {
  const isActive = flags.isActive ?? row.isActive
  const isFrozen = flags.isFrozen ?? row.isFrozen
  const depositsEnabled = flags.depositsEnabled ?? row.depositsEnabled

  let gating: EarnGating | undefined
  let reason: string | undefined

  if (isActive === false) {
    gating = 'paused'
    reason = 'Market is inactive'
  } else if (isFrozen === true) {
    gating = 'frozen'
    reason = 'Market is frozen — no new deposits'
  } else if (depositsEnabled === false) {
    gating = 'paused'
    reason = 'Deposits are disabled for this market'
  } else if (isSupplyCapFull(row)) {
    gating = 'cap-full'
    reason = 'Supply cap is full'
  }

  const blocked = gating !== undefined

  // Earns nothing, but the deposit works — flagged rather than blocked.
  if (!blocked && totalRate === 0) {
    gating = 'collateral-only'
    reason = 'Collateral-only market — deposits earn no yield'
  }

  return {
    // Withdrawals stay open through a freeze and a full cap; only a hard
    // inactive market shuts them.
    canDeposit: !blocked,
    canWithdraw: isActive !== false,
    gating,
    reason,
  }
}

/**
 * `supplyCap` and `totalDeposits` are both in TOKEN units here, so they compare
 * directly. A cap of 0 means "uncapped" across every lender that reports one —
 * never "no room".
 */
function isSupplyCapFull(row: PoolSourceRow): boolean {
  const cap = num(row.caps?.supplyCap) ?? num(row.supplyCap)
  if (cap === undefined || cap <= 0) return false
  const deposits = num(row.totalDeposits)
  if (deposits === undefined) return false
  return deposits >= cap
}

function sumRewardAprs(rewards: PoolSourceRow['rewards']): number | undefined {
  if (!Array.isArray(rewards) || rewards.length === 0) return undefined
  let total = 0
  let seen = false
  for (const r of rewards) {
    const apr = num(r?.depositRate)
    if (apr === undefined) continue
    total += apr
    seen = true
  }
  return seen ? total : undefined
}

function amount(formatted: unknown, usd: unknown): EarnAmount {
  return { formatted: num(formatted), usd: num(usd) }
}

function sum(...parts: Array<number | undefined>): number | undefined {
  const present = parts.filter((p): p is number => p !== undefined)
  return present.length ? present.reduce((a, b) => a + b, 0) : undefined
}
